import { lazy, Suspense, type ReactElement } from 'react';
import { Center, Loader } from '@mantine/core';
import { createBrowserRouter } from 'react-router-dom';
import { Layout } from './Layout.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';

/**
 * Lazy-load every non-Dashboard page. Dashboard is the default route
 * so staff hit it on cold open; all others chunk out and stream on
 * demand. Cuts the entry bundle from ~380 KB gz to ~200 KB gz (Mantine
 * charts, datepicker, color picker only pulled when needed).
 *
 * Each lazy boundary is wrapped in Suspense with a spinner — no blank
 * screen during chunk fetch.
 */
const ReservationsPage = lazy(() =>
  import('./pages/ReservationsPage.tsx').then((m) => ({ default: m.ReservationsPage })),
);
const CustomersPage = lazy(() =>
  import('./pages/CustomersPage.tsx').then((m) => ({ default: m.CustomersPage })),
);
const CustomerDetailPage = lazy(() =>
  import('./pages/CustomerDetailPage.tsx').then((m) => ({ default: m.CustomerDetailPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage.tsx').then((m) => ({ default: m.SettingsPage })),
);
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage.tsx').then((m) => ({ default: m.AnalyticsPage })),
);
const AuditPage = lazy(() =>
  import('./pages/AuditPage.tsx').then((m) => ({ default: m.AuditPage })),
);
const NotificationLogsPage = lazy(() =>
  import('./pages/NotificationLogsPage.tsx').then((m) => ({ default: m.NotificationLogsPage })),
);
const StaffPage = lazy(() =>
  import('./pages/StaffPage.tsx').then((m) => ({ default: m.StaffPage })),
);
const PosPage = lazy(() => import('./pages/PosPage.tsx').then((m) => ({ default: m.PosPage })));
const PaymentsOverviewPage = lazy(() =>
  import('./pages/PaymentsOverviewPage.tsx').then((m) => ({ default: m.PaymentsOverviewPage })),
);
const PaymentsChargesPage = lazy(() =>
  import('./pages/PaymentsChargesPage.tsx').then((m) => ({ default: m.PaymentsChargesPage })),
);
const PaymentsRefundsPage = lazy(() =>
  import('./pages/PaymentsRefundsPage.tsx').then((m) => ({ default: m.PaymentsRefundsPage })),
);
const PaymentsGatewaysPage = lazy(() =>
  import('./pages/PaymentsGatewaysPage.tsx').then((m) => ({ default: m.PaymentsGatewaysPage })),
);
const PaymentsExpensesPage = lazy(() =>
  import('./pages/PaymentsExpensesPage.tsx').then((m) => ({ default: m.PaymentsExpensesPage })),
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage.tsx').then((m) => ({ default: m.NotFoundPage })),
);

function LazyBoundary({ children }: { children: ReactElement }): ReactElement {
  return (
    <Suspense
      fallback={
        <Center py="xl" style={{ minHeight: '40vh' }}>
          <Loader size="md" />
        </Center>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // /login is outside the Layout wrapper — no nav/auth-gated shell.
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: <Layout />,
    errorElement: (
      <Layout>
        <LazyBoundary>
          <NotFoundPage />
        </LazyBoundary>
      </Layout>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      {
        path: 'reservations',
        element: (
          <LazyBoundary>
            <ReservationsPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'customers',
        element: (
          <LazyBoundary>
            <CustomersPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'customers/:id',
        element: (
          <LazyBoundary>
            <CustomerDetailPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyBoundary>
            <SettingsPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'analytics',
        element: (
          <LazyBoundary>
            <AnalyticsPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'audit',
        element: (
          <LazyBoundary>
            <AuditPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'notifications',
        element: (
          <LazyBoundary>
            <NotificationLogsPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'staff',
        element: (
          <LazyBoundary>
            <StaffPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'pos',
        element: (
          <LazyBoundary>
            <PosPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'payments',
        element: (
          <LazyBoundary>
            <PaymentsOverviewPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'payments/charges',
        element: (
          <LazyBoundary>
            <PaymentsChargesPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'payments/refunds',
        element: (
          <LazyBoundary>
            <PaymentsRefundsPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'payments/gateways',
        element: (
          <LazyBoundary>
            <PaymentsGatewaysPage />
          </LazyBoundary>
        ),
      },
      {
        path: 'payments/expenses',
        element: (
          <LazyBoundary>
            <PaymentsExpensesPage />
          </LazyBoundary>
        ),
      },
      {
        path: '*',
        element: (
          <LazyBoundary>
            <NotFoundPage />
          </LazyBoundary>
        ),
      },
    ],
  },
]);
